// import { useTranslation } from 'react-i18next';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import React from 'react';
import FinancingStatusMessage from 'Modules/patientfi/components/FinancingStatusMessage';

import { useGetErrorConfig } from 'Modules/patientfi/hooks/useGetErrorConfig';
import CreateApplication from 'Modules/patientfi/routes/CreateApplication';
import FinancingRequalify from 'Modules/patientfi/routes/FinancingRequalify';
import FinancingDeclined from 'Modules/patientfi/routes/FinancingDeclined';
import FinancingVerificationRequired from 'Modules/patientfi/routes/FinancingVerificationRequired';
import FinancingSupportRequired from 'Modules/patientfi/routes/FinancingSupportRequired';
import { ACCOUNT_DETAILS_MISMATCH } from 'Modules/patientfi/config/appStatusMap';

const isFrozenCreditCase = (application, nextAction) => {
  const correctionRequired = application?.payload?.correction_required;
  return (
    nextAction === 'CORRECTION_REQUIRED' && correctionRequired?.message?.includes('{FROZEN_CREDIT}')
  );
};

const isReviewDeclinedCase = (application, nextAction) => {
  const correctionRequired = application?.payload?.correction_required;
  return (
    nextAction === 'DECISION_PENDING' &&
    correctionRequired?.status === 'REVIEW' &&
    correctionRequired?.message ===
      'Invalid request for application-requalify: Application status must be incomplete'
  );
};

const isIdVerificationFailedCase = (application, nextAction) => {
  const correctionRequired = application?.payload?.correction_required;
  return (
    nextAction === 'DECLINED' &&
    correctionRequired?.status === 'REJECTED' &&
    correctionRequired?.message === 'truvalidateIDVFailed'
  );
};

const isAccountDetailsMismatchCase = (application) =>
  application?.payload?.correction_required?.status === ACCOUNT_DETAILS_MISMATCH;

const isSupportRequiredCase = (application) =>
  Boolean(application?.payload?.existing_account?.available_actions?.includes('REFRESH'));

export default function FinancingExceptions({ customer_id, application, nextAction }) {
  document.title = 'Payment Portal - Financing';
  useScrollTop();

  const purpose =
    nextAction === 'DECISION_PENDING' || isAccountDetailsMismatchCase(application)
      ? 'create'
      : 'update';
  const errorConfig = useGetErrorConfig(application);

  if (isSupportRequiredCase(application)) {
    return <FinancingSupportRequired customer_id={customer_id} application={application} />;
  }

  // Check for frozen credit case - redirect to requalify flow
  if (isFrozenCreditCase(application, nextAction)) {
    return <FinancingRequalify />;
  }

  // Check for review/declined case - show declined page without reapply option
  if (isReviewDeclinedCase(application, nextAction)) {
    return <FinancingDeclined allowCreateApplication={false} application={application} />;
  }

  // Check for ID verification failed case - show verification required flow
  if (isIdVerificationFailedCase(application, nextAction)) {
    return <FinancingVerificationRequired />;
  }

  // For DECLINED cases that don't match specific exceptions, show declined page
  if (nextAction === 'DECLINED') {
    return <FinancingDeclined application={application} />;
  }

  if (errorConfig?.message) {
    return (
      <CreateApplication
        type="primary"
        customer_id={customer_id}
        purpose={purpose}
        errorConfig={errorConfig}
      />
    );
  }

  return <FinancingStatusMessage nextAction={nextAction} message={errorConfig.message} />;
}
